'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { Prompt } from '@/types';
import PromptDetailModal from '@/components/prompt/PromptDetailModal';
import { useAIModels } from '@/lib/ai-models-context';

const CATEGORY_DOT: Record<string, string> = {
  Writing:   'oklch(0.75 0.14 160)',
  Coding:    'oklch(0.72 0.14 230)',
  Art:       'oklch(0.72 0.15 285)',
  Business:  'oklch(0.78 0.14 75)',
  Education: 'oklch(0.75 0.14 40)',
  Other:     'oklch(0.58 0.008 85)',
};

function formatStat(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
  return String(n);
}

export default function PromptListRow({ prompt }: { prompt: Prompt }) {
  const router = useRouter();
  const { models: aiModels } = useAIModels();
  const [imgError, setImgError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const aiModelById = useMemo(() => new Map(aiModels.map((m) => [m.id, m])), [aiModels]);
  const displayImage = (prompt.images && prompt.images.length > 0 ? prompt.images[0] : undefined) ?? prompt.cover_image_url;
  const hasImage = !!displayImage && !imgError;
  const dot = CATEGORY_DOT[prompt.category || 'Other'] ?? CATEGORY_DOT.Other;
  const visibleModels = (prompt.models ?? []).slice(0, 2);
  const extraModelCount = (prompt.models?.length ?? 0) > 2 ? (prompt.models!.length - 2) : 0;

  // Same UX as PromptCard: left-click → modal; cmd/ctrl/middle-click → full page.
  const handleClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      router.push(`/prompt/${prompt.id}`);
      return;
    }
    e.preventDefault();
    setModalOpen(true);
  };

  return (
    <>
    <Link href={`/prompt/${prompt.id}`} className="no-underline" onClick={handleClick}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '48px 1fr auto',
          gap: 16, alignItems: 'center',
          padding: '14px 18px',
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-md)',
          transition: 'border-color 0.12s',
        }}
        className="hover:border-ink-3"
      >
        <div style={{
          width: 48, height: 48,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-soft)',
          overflow: 'hidden', position: 'relative', flex: 'none',
        }}>
          {hasImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={displayImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => setImgError(true)} />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'grid', placeItems: 'center',
              fontFamily: 'var(--font-mono)', fontSize: 10,
              color: 'var(--ink-3)',
            }}>
              {(prompt.category || 'Other').slice(0, 2).toUpperCase()}
            </div>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 500,
            letterSpacing: '-0.01em', color: 'var(--ink)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {prompt.title}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {prompt.description || prompt.content.slice(0, 80)}
          </div>
        </div>
        <div style={{
          display: 'flex', gap: 14, alignItems: 'center',
          fontSize: 11.5, color: 'var(--ink-3)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 8px', fontSize: 11, fontWeight: 500,
            color: 'var(--ink-2)',
            background: 'var(--bg-soft)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-full)',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />
            {prompt.category || 'Other'}
          </span>
          {visibleModels.map((id) => {
            const m = aiModelById.get(id);
            return (
              <span key={id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', fontSize: 11, fontWeight: 500,
                borderRadius: 'var(--radius-full)',
                background: m ? `${m.color}18` : 'var(--bg-soft)',
                color: m?.color ?? 'var(--ink-3)',
                border: `1px solid ${m?.color ?? 'var(--line)'}33`,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: m?.color ?? 'var(--ink-3)', flexShrink: 0 }} />
                {m?.name ?? id}
              </span>
            );
          })}
          {extraModelCount > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '3px 8px', fontSize: 11, fontWeight: 500,
              borderRadius: 'var(--radius-full)',
              background: 'var(--bg-soft)', color: 'var(--ink-3)',
              border: '1px solid var(--line)',
            }}>
              +{extraModelCount}
            </span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={`${prompt.save_count.toLocaleString()} saves`}>
            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>bookmark</span>
            {formatStat(prompt.save_count)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={`${(prompt.copy_count ?? 0).toLocaleString()} copies`}>
            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>content_copy</span>
            {formatStat(prompt.copy_count ?? 0)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={`${prompt.view_count.toLocaleString()} views`}>
            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>visibility</span>
            {formatStat(prompt.view_count)}
          </span>
        </div>
      </div>
    </Link>
    {modalOpen && (
      <PromptDetailModal
        prompt={prompt}
        onClose={() => setModalOpen(false)}
      />
    )}
    </>
  );
}
